import ExcelJs from 'exceljs';
import { saveAs } from 'file-saver';
import { cloneDeep } from 'lodash-es';
import i18n from '@/lang';
import { QUOTATION_MODEL } from '@/views/finance/utils';
import { ISMIX_START } from '@/views/finance/enums';

/**
 * @type {TTableRow}
 * @desc 模板
 */
export const TMP_OBJ = {
  id: undefined,
  partitionAffiliateId: undefined, // 车线id 文件导入的时候才存在
  vehicleType: '', // 车型
  isMix: 0, // 是否混配
  quotationMode: '', // 报价模式
  startDistance: '', // 里程开始
  endDistance: '', //  里程结束
  feeTypeCode: '', // 费用类型
  price: '' // 价格
};

// 抽离cloneDeep
const deepCloneArray = (arr) => cloneDeep(arr);

// #region set设置数据
export const { notSetPartitionFun, setPartitionFun } = (() => {
  /**
   * 没有选择分区
   * @param {any[]} arr 导入的数据
   * @param {TTableRow[]} defaultArr 列表中已经存在的数据
   * @returns {TTableRow[]}
   */
  const notSetPartitionFun = (arr, defaultArr) => {
    // 导入的数据处理
    const list = arr.map(item => {
      // 普通规则
      const { id, partitionAffiliateId, startDistance, endDistance, price, feeTypeCode, vehicleType } = item;
      return {
        ...TMP_OBJ,
        id, partitionAffiliateId, startDistance, endDistance, price, feeTypeCode, vehicleType
      };
    });

    const defaultList = deepCloneArray(defaultArr);
    console.log(list, '导入的数据', defaultList);

    // 当前为空
    if (defaultList.length === 0) {
      return list;
    }

    const listNotId = []; // 导入的列表不存在id

    list.forEach(item => {
      listNotId.push(item);
    });

    return [...defaultList, ...listNotId];
  };

  /**
   * 选择了车线
   * @param {any[]} arr 返回报价明细
   * @param {TTableObj} partitionRow 初始化报价数据
   * @returns {TTableObj}
   */
  const setPartitionFun = (arr, partitionRow) => {
    const lineObj = deepCloneArray(partitionRow);
    console.log(lineObj, '初始化报价数据', arr);
    function updateArr(arr, item) {
      arr.push({
        id: item.id,
        vehicleType: item.vehicleType,
        isMix: item.isMix || 0,
        quotationMode: item.quotationMode,
        startDistance: item.startDistance,
        endDistance: item.endDistance,
        feeTypeCode: item.feeTypeCode,
        price: item.price,
        createTime: item.createTime
      });
    }
    // 初始化
    const result = {};
    arr.forEach(item => {
      // 车线id
      const partitionKey = String(item.lineId);
      // 初始化分组结构
      if (!result[partitionKey]) {
        result[partitionKey] = {
          arr: []
        };
      }
      console.log(result, '分组结构', partitionKey);

      const group = result[partitionKey];
      updateArr(group.arr, item);
    });
    return result;
  };

  return {
    notSetPartitionFun, setPartitionFun
  };
})();

// #region 导入导出
export const { downloadTemplate, importTemplate, downloadQuotation, SEPARATOR } = (() => {
  const SEPARATOR = '->'; // 分隔符

  // 模板标题
  const headArr = [
    {
      header: i18n.t('basicData.routeName') + '*', // 车线
      key: 'partitionAffiliateId',
      width: 30
    },
    {
      header: i18n.t('vehicle.modelLabel') + '*', // 车型
      key: 'vehicleType',
      width: 30
    },
    {
      header: i18n.t('basicData.WhetherToMixQuotes') + '*', // 混合报价
      key: 'isMix',
      width: 30
    },
    {
      header: i18n.t('basicData.报价模式') + '*', // 报价模式
      key: 'quotationMode',
      width: 40
    },
    {
      header: i18n.t('basicData.KilometersStartKM') + '*', // 公里开始(KM) 不包含
      key: 'startDistance',
      width: 40
    },
    {
      header: i18n.t('basicData.MileageEndKM') + '*', // 公里结束(KM) 包含
      key: 'endDistance',
      width: 40
    },
    {
      header: i18n.t('basicData.ExpenseType') + '*', // 费用类型
      key: 'feeTypeCode',
      width: 40
    },
    {
      header: i18n.t('basicData.Price') + '*', // 价格
      key: 'price',
      width: 30
    },
    {
      header: 'ID', // id 下载报价的时候存在
      key: 'id',
      width: 20
    }
  ];
  // 车线
  const PartitionArr = [
    {
      header: i18n.t('basicData.routeName'), // 车线
      key: 'partitionAffiliateId',
      width: 40
    },
    {
      header: i18n.t('vehicle.modelLabel'), // 车型
      key: 'vehicleType',
      width: 40
    },
    {
      header: i18n.t('basicData.报价模式'), // 报价模式
      key: 'quotationMode',
      width: 40
    },
    {
      header: i18n.t('basicData.WhetherToMixQuotes'), // 混合报价
      key: 'isMix',
      width: 40
    }
  ];

  /**
   * 获取头部，选择与未选择分区，对于索引为0的标题有区别
   *   如果选择了分区则 索引为0 需要加上 *
   *   @returns {any[]}
   */
  const getHeadObj = () => {
    return deepCloneArray(headArr);
  };

  /**
   * 创建模板
   * @param stitchingLine 车线
   * @param feeTypeList 费用类型
   * @param modelList 车型
   * @param sheet1RowArr 报价明细
   * @returns workbook
   */
  const createTemplate = (stitchingLine, modelList, feeTypeList, sheet1RowArr = []) => {
    const wb = new ExcelJs.Workbook();
    const Sheet1 = wb.addWorksheet('Sheet1'); // 报价明细
    const Sheet2 = wb.addWorksheet('Sheet2'); // 车线
    const Sheet3 = wb.addWorksheet('Sheet3'); // 车型
    const Sheet4 = wb.addWorksheet('Sheet4'); // 报价模式
    const Sheet5 = wb.addWorksheet('Sheet5'); // 费用类型
    const Sheet6 = wb.addWorksheet('Sheet6');

    console.log(stitchingLine, modelList, feeTypeList, sheet1RowArr, '----------------下载报价');
    // 标头处理
    Sheet1.columns = getHeadObj();
    Sheet1.getRow(1).font = {
      color: { argb: '000000' },
      bold: true
    };
    // 提示
    Sheet1.mergeCells('A2:H2');
    Sheet1.getCell('A2').value = '提示:标记*必填；选择报价为混合模式，则可选里程实际报价及里程偏大值报价，否则只可选择单一报价模式\n' +
      'Observação: Campos marcados com * são obrigatórios; Se o modo de cotação mista for selecionado, você pode escolher entre a cotação de quilometragem real e a cotação de quilometragem maior, caso contrário, apenas o modo de cotação única está disponível.';
    Sheet1.getRow(2).height = 50;
    Sheet1.getRow(2).getCell(1).style = {
      font: {
        color: { argb: 'ff0000' }
      },
      alignment: {
        vertical: 'center', // 垂直居中
        horizontal: 'left', // 水平左对齐
        wrapText: true, // 自动换行
        indent: 0 // 禁止缩进
      }
    };

    // 下载报价的时候
    if (sheet1RowArr.length) {
      // 数据处理
      const sheetData = sheet1RowArr.map((item) => {
        return {
          ...item,
          vehicleType: modelList.find(items => items.dictValue === item.vehicleType)?.dictLabel + SEPARATOR + item.vehicleType,
          feeTypeCode: feeTypeList.find(items => items.code === item.feeTypeCode)?.displayName + SEPARATOR + item.feeTypeCode,
          quotationMode: QUOTATION_MODEL[item.quotationMode]?.label + SEPARATOR + item.quotationMode,
          isMix: ISMIX_START[item.isMix]?.label + SEPARATOR + item.isMix
        };
      });
      Sheet1.addRows(sheetData);
    }
    // 车线列表
    Sheet2.columns = deepCloneArray([PartitionArr[0]]);
    const Sheet2_data = [];
    if (stitchingLine && stitchingLine.length > 0) {
      stitchingLine.forEach((item) => {
        const { id, name } = item;
        Sheet2_data.push({
          partitionAffiliateId: `${name}${SEPARATOR}${id}`
        });
      });
    }
    Sheet2.addRows(Sheet2_data);
    // 车型列表
    Sheet3.columns = deepCloneArray([PartitionArr[1]]);
    const Sheet3_data = [];
    if (modelList && modelList.length > 0) {
      modelList.forEach((item) => {
        const { dictValue, dictLabel } = item;
        Sheet3_data.push({
          vehicleType: `${dictLabel}${SEPARATOR}${dictValue}`
        });
      });
    }
    Sheet3.addRows(Sheet3_data);
    // 报价模式
    Sheet4.columns = deepCloneArray([PartitionArr[2]]);
    const Sheet4_data = [];
    for (const key in QUOTATION_MODEL) {
      Sheet4_data.push({
        quotationMode: QUOTATION_MODEL[key].label + '->' + QUOTATION_MODEL[key].value
      });
    }
    Sheet4.addRows(Sheet4_data);
    // 费用类型
    Sheet5.columns = [
      {
        header: i18n.t('basicData.ExpenseType') + '(' + i18n.t('ChannelManage.name') + ')', // 费用类型
        key: 'feeTypeName',
        width: 20
      },
      {
        header: i18n.t('basicData.ExpenseType') + '(code)', // 费用类型
        key: 'feeTypeCode',
        width: 20
      }
    ];
    const Sheet5_data = [];
    feeTypeList.map((item) => {
      Sheet5_data.push({
        feeTypeCode: item.code,
        feeTypeName: item.displayName + '->' + item.code
      });
    });
    Sheet5.addRows(Sheet5_data);
    // 混合报价
    Sheet6.columns = deepCloneArray([PartitionArr[3]]);
    const Sheet6_data = [];
    for (const key in ISMIX_START) {
      Sheet6_data.push({
        isMix: ISMIX_START[key].label + '->' + ISMIX_START[key].value
      });
    }
    Sheet6.addRows(Sheet6_data);

    new Array(50000).fill(0).forEach((_, idx) => {
      const row = idx + 3;

      // 车线名称，如果存在则设置
      Sheet1.getCell(row, 1).dataValidation = {
        type: 'list',
        formulae: [`=Sheet2!$A$2:$A${Sheet2_data.length + 1}`]
      };

      // 车型名称，如果存在则设置
      Sheet1.getCell(row, 2).dataValidation = {
        type: 'list',
        formulae: [`=Sheet3!$A$2:$A${Sheet3_data.length + 1}`]
      };

      // 报价模式
      Sheet1.getCell(row, 4).dataValidation = {
        type: 'list',
        formulae: [`=Sheet4!$A$2:$A${Sheet4_data.length + 1}`]
      };

      // 费用类型
      Sheet1.getCell(row, 7).dataValidation = {
        type: 'list',
        formulae: [`=Sheet5!$A$2:$A${Sheet5_data.length + 1}`]
      };

      // 混合报价
      Sheet1.getCell(row, 3).dataValidation = {
        type: 'list',
        formulae: [`=Sheet6!$A$2:$A${Sheet6_data.length + 1}`]
      };
    });

    return wb;
  };

  /**
   * 下载模板
   * @param stitchingLine 车线
   * @param modelList 车型
   * @param feeTypeList 费用类型
   * @returns {Promise<void>}
   */
  const downloadTemplate = async(stitchingLine, modelList, feeTypeList) => {
    const wb = createTemplate(stitchingLine, modelList, feeTypeList);

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/octet-stream'
    });

    const saveName = `${i18n.t('basicData.ImportQuotationTemplatesInBulk')}.xlsx`;
    saveAs(blob, saveName);
  };

  /**
   * 导入模板处理
   * @param {any[]} arr 导入的数据
   * @returns {TTableRow[]}
   */
  const importTemplate = (arr) => {
    console.log('导入的数据', arr);
    const headArr = getHeadObj();

    const list = [];
    arr.forEach(item => {
      const obj = { ...TMP_OBJ };

      Object.entries(item).forEach(([key, value]) => {
        for (let i = 0; i < headArr.length; i++) {
          const item = headArr[i];
          if (!item || !item.header) continue;
          if (key === item.header) {
            // 需要解析字段
            if ([0, 1, 2, 3, 6].includes(i)) {
              const parts = typeof value === 'string' ? value.split(SEPARATOR) : [];
              if (parts.length > 1) {
                obj[item.key] = [2, 3].includes(i) ? Number(parts[1]) : parts[1];
              }
            } else {
              obj[item.key] = value >= 0 ? value : undefined;
            }
          }
        }
      });

      list.push(obj);
    });

    return list;
  };

  /**
   * 下载报价
   * @param list 明细详情
   * @param stitchingLine 车线
   * @param modelList 车型
   * @param feeTypeList 费用类型
   * @returns {Promise<void>}
   */
  const downloadQuotation = async(list, stitchingLine, modelList, feeTypeList) => {
    const wb = createTemplate(stitchingLine, modelList, feeTypeList, list);

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/octet-stream'
    });

    const saveName = `${i18n.t('basicData.QuoteDetails')}.xlsx`;
    saveAs(blob, saveName);
  };

  return {
    downloadTemplate,
    importTemplate,
    downloadQuotation,
    SEPARATOR
  };
})();

/**
 * 校验
 * @param arr 数组
 * @returns {this is *[]|boolean}
 */
export const validate = (arr = []) => {
  // 空数组直接返回true
  if (arr.length === 0) {
    return true;
  }

  // 校验每个元素是否符合规则
  return arr.every((item) => {
    const { vehicleType, quotationMode, startDistance, endDistance, feeTypeCode, price } = item;

    // 校验车型
    if (!vehicleType) {
      return false;
    }

    // 校验报价模式
    if (!quotationMode) {
      return false;
    }

    // 校验费用类型
    if (!feeTypeCode) {
      return false;
    }

    // 校验价格
    if (!price) {
      return false;
    }

    // 校验
    if ([5, 6].includes(quotationMode)) {
      return !(!startDistance || !endDistance);
    }

    // 校验计费规则：必须是有效的正小数
    return !(!price || price <= 0);
  });
};

/**
 * 判断键值是否一致
 * @param arr 数组
 * @param key 键
 * @returns {*|boolean}
 */
export const isKeyValueConsistent = (arr, key) => {
  if (!arr.length) return true;

  const firstValue = arr[0][key];
  return arr.every(item => item[key] === firstValue);
};
